import { useState, type FormEvent } from "react";
import { useOnboardingForm } from "../useOnboardingForm";
import { RadioCard, StepHeading, StepShell } from "@codel/dova-ds";
import { TextField } from "../../components/TextField";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";
import type { RelationshipStatus } from "../../types";
// The options are NOT declared here. A key persists as a bare token, so its label
// is the only record of what it means — it lives in `answerVocabulary.ts` under
// the lock that stops a rename from silently remapping stored answers.
import { RELATIONSHIP_STATUS_OPTIONS as STATUS_OPTIONS , FREE_TEXT_QUESTION } from "../answerVocabulary";

export function S4aStatus({ onNext, onBack, currentStepIndex, totalSteps }: StepProps) {
  const { state, updateAnswers } = useOnboardingForm();
  const [status, setStatus] = useState<RelationshipStatus | null>(state.answers.relationshipStatus);
  const [otherText, setOtherText] = useState(
    state.answers.relationshipStatusOther ?? ""
  );
  const [error, setError] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!status) {
      setError("Pick one to continue.");
      return;
    }
    setError("");
    updateAnswers({
      relationshipStatus: status,
      relationshipStatusOther:
        status === "other" ? otherText.trim() || null : null,
    });
    onNext();
  };

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onSubmit={handleSubmit}
      footer={<Button type="submit">Continue</Button>}
    >
      <StepHeading title="How would you describe your relationship?" />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          {STATUS_OPTIONS.map((opt) => (
            <RadioCard
              key={opt.value}
              name="relationship_status"
              value={opt.value}
              label={opt.label}
              checked={status === opt.value}
              onChange={(v) => {
                setStatus(v as RelationshipStatus);
                if (error) setError("");
              }}
            />
          ))}
        </div>
        {status === "other" && (
          <TextField
            id="relationship-status-other"
            aria-label={FREE_TEXT_QUESTION.relationship_status_other}
            value={otherText}
            onChange={setOtherText}
            placeholder="How would you describe it?"
            autoFocus
          />
        )}
        {error && (
          <p className="text-xs leading-[1.5] text-[var(--danger)]">{error}</p>
        )}
      </div>
    </StepShell>
  );
}
